import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { stepFamilyMembersSchema, type StepFamilyMembersForm } from './schemas';
import { useRegistrationStore } from '../../store/registrationStore';
import { Button } from '../Button';

const inputClasses =
  'w-full bg-slate-50 border border-slate-200 rounded-lg p-3 text-slate-700 placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-primary transition-all outline-none';
const labelClasses = 'block text-sm font-bold text-slate-700 mb-1.5';
const errorClasses = 'text-xs text-error mt-1';

const AGE_GROUPS = [
  { value: 'adult', label: 'Adult (18+)' },
  { value: 'teen', label: 'Teen (13-17)' },
  { value: 'youth', label: 'Youth (6-12)' },
  { value: 'child', label: 'Child (3-5)' },
  { value: 'infant', label: 'Infant/Toddler (2 and under)' },
];

const RELATIONSHIPS = [
  'Self',
  'Spouse',
  'Partner',
  'Son',
  'Daughter',
  'Parent',
  'Sibling',
  'Grandparent',
  'Grandchild',
  'Niece',
  'Nephew',
  'Cousin',
  'In-Law',
  'Other',
];

interface StepFamilyMembersProps {
  onNext: () => void;
  onBack: () => void;
}

export function StepFamilyMembers({ onNext, onBack }: StepFamilyMembersProps) {
  const { familyMembers, setFamilyMembers } = useRegistrationStore();

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<StepFamilyMembersForm>({
    resolver: zodResolver(stepFamilyMembersSchema),
    defaultValues: {
      familyMembers:
        familyMembers.length > 0
          ? familyMembers.map((fm) => ({
              firstName: fm.firstName,
              lastName: fm.lastName,
              ageGroup: fm.ageGroup,
              relationship: fm.relationship,
              email: (fm as Record<string, unknown>).email as string || '',
            }))
          : [{ firstName: '', lastName: '', ageGroup: 'adult' as const, relationship: '', email: '' }],
    },
  });

  const { fields, append, remove } = useFieldArray({ control, name: 'familyMembers' });

  const onSubmitStep = (data: StepFamilyMembersForm) => {
    // Merge with existing member data (preserve dietary/allergy data from step 4)
    const merged = data.familyMembers.map((fm, i) => ({
      ...familyMembers[i],
      ...fm,
      email: fm.email || null,
      dietaryRestrictions: familyMembers[i]?.dietaryRestrictions || [],
      foodAllergies: familyMembers[i]?.foodAllergies || null,
      accessibilityNeeds: familyMembers[i]?.accessibilityNeeds || null,
      profileImageUrl: familyMembers[i]?.profileImageUrl || null,
    }));
    setFamilyMembers(merged);
    onNext();
  };

  return (
    <form onSubmit={handleSubmit(onSubmitStep)} className="space-y-6">
      <h2 className="text-xl font-bold text-slate-900">Family Members</h2>
      <p className="text-sm text-slate-600">
        Add everyone attending the reunion, including yourself.
      </p>

      {fields.map((field, index) => (
        <div key={field.id} className="border border-slate-200 rounded-lg p-4 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-bold text-slate-700">Member {index + 1}</h3>
            {fields.length > 1 && (
              <button
                type="button"
                onClick={() => remove(index)}
                className="text-xs text-error hover:underline"
              >
                Remove
              </button>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor={`fm-first-${index}`} className={labelClasses}>
                First Name
              </label>
              <input
                id={`fm-first-${index}`}
                className={inputClasses}
                {...register(`familyMembers.${index}.firstName`)}
              />
              {errors.familyMembers?.[index]?.firstName && (
                <p className={errorClasses}>
                  {errors.familyMembers[index].firstName.message}
                </p>
              )}
            </div>
            <div>
              <label htmlFor={`fm-last-${index}`} className={labelClasses}>
                Last Name
              </label>
              <input
                id={`fm-last-${index}`}
                className={inputClasses}
                {...register(`familyMembers.${index}.lastName`)}
              />
              {errors.familyMembers?.[index]?.lastName && (
                <p className={errorClasses}>
                  {errors.familyMembers[index].lastName.message}
                </p>
              )}
            </div>
            <div>
              <label htmlFor={`fm-age-${index}`} className={labelClasses}>
                Age Group
              </label>
              <select
                id={`fm-age-${index}`}
                className={inputClasses}
                {...register(`familyMembers.${index}.ageGroup`)}
              >
                {AGE_GROUPS.map((ag) => (
                  <option key={ag.value} value={ag.value}>
                    {ag.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor={`fm-rel-${index}`} className={labelClasses}>
                Relationship
              </label>
              <select
                id={`fm-rel-${index}`}
                className={inputClasses}
                {...register(`familyMembers.${index}.relationship`)}
              >
                <option value="">Select...</option>
                {RELATIONSHIPS.map((r) => (
                  <option key={r} value={r.toLowerCase()}>
                    {r}
                  </option>
                ))}
              </select>
              {errors.familyMembers?.[index]?.relationship && (
                <p className={errorClasses}>
                  {errors.familyMembers[index].relationship.message}
                </p>
              )}
            </div>
          </div>
          {/* Email field - full width below the grid */}
          <div>
            <label htmlFor={`fm-email-${index}`} className={labelClasses}>
              Email <span className="font-normal text-slate-400">(optional)</span>
            </label>
            <input
              id={`fm-email-${index}`}
              type="email"
              className={inputClasses}
              placeholder="They'll receive an invite to join"
              {...register(`familyMembers.${index}.email`)}
            />
            {errors.familyMembers?.[index]?.email && (
              <p className={errorClasses}>
                {errors.familyMembers[index].email.message}
              </p>
            )}
          </div>
        </div>
      ))}

      <button
        type="button"
        onClick={() =>
          append({ firstName: '', lastName: '', ageGroup: 'adult', relationship: '', email: '' })
        }
        className="text-sm text-primary font-medium hover:underline"
      >
        + Add Another Member
      </button>

      {errors.familyMembers?.root && (
        <p className={errorClasses}>{errors.familyMembers.root.message}</p>
      )}

      <div className="flex justify-between">
        <Button type="button" variant="outline" onClick={onBack}>
          Back
        </Button>
        <Button type="submit" variant="primary">
          Next: Room Accommodations
        </Button>
      </div>
    </form>
  );
}
